import React from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    CustomInput,
    FormGroup,
    Row,
    Label,
} from 'reactstrap';
// Import constants
import { ALL_PERMISSIONS, getFeatureLabel, getPermissionLabel } from 'constants/permissionLabels';

const RolePermissionsPicker = ({
    catalog,
    value = [],
    onChange,
    disabled = false,
    formType = 'create',
}) => {
    const selected = new Set(value || []);
    const isFullAccess = selected.has(ALL_PERMISSIONS);

    // Emit the change event to the parent component to update the selected permissions
    const emitChange = (nextSet) => {
        onChange(Array.from(nextSet));
    };

    // Toogle full access permission
    const toggleFullAccess = () => {
        if (isFullAccess) {
            emitChange(new Set());
        } else {
            emitChange(new Set([ALL_PERMISSIONS]));
        }
    };

    // Toogle individual permission
    const togglePermission = (permissionId) => {
        if (disabled || isFullAccess) {
            return;
        }
        const next = new Set(selected);
        if (next.has(permissionId)) {
            next.delete(permissionId);
        } else {
            next.add(permissionId);
        }
        next.delete(ALL_PERMISSIONS);
        emitChange(next);
    };

    // Toogle all permissions for a feature
    const toggleFeatureAll = (permissions) => {
        if (disabled || isFullAccess) {
            return;
        }
        const next = new Set(selected);
        next.delete(ALL_PERMISSIONS);
        const allSelected = permissions.every((p) => next.has(p));
        permissions.forEach((p) => {
            if (allSelected) {
                next.delete(p);
            } else {
                next.add(p);
            }
        });
        emitChange(next);
    };

    // If no catalog, return null
    if (!catalog || catalog.length === 0) {
        return null;
    }

    return (
        <>
            <FormGroup check className="mb-3">
                <CustomInput
                    type="checkbox"
                    id={`perm-all-access-${formType}`}
                    label="Acceso total (administrador)"
                    checked={isFullAccess}
                    onChange={toggleFullAccess}
                    disabled={disabled}
                />
            </FormGroup>
            {/* Render the features and permissions */}
            {catalog.map((feature) => {
                const featureSelected = feature.permissions.filter((p) => selected.has(p));
                const allFeatureSelected =
                    feature.permissions.length > 0 &&
                    featureSelected.length === feature.permissions.length;

                return (
                    <Card key={feature.feature_id} className="mb-3">
                        <CardHeader className="py-2">
                            <Row className="align-items-center">
                                <Col>
                                    <span className="text-sm font-weight-bold">
                                        {getFeatureLabel(feature.feature_id)}
                                    </span>
                                    <small className="text-muted ml-2">
                                        ({featureSelected.length}/{feature.permissions.length})
                                    </small>
                                </Col>
                                <Col xs="auto">
                                    <CustomInput
                                        type="checkbox"
                                        id={`feature-all-${feature.feature_id}-${formType}`}
                                        label="Seleccionar todos"
                                        checked={allFeatureSelected}
                                        onChange={() => toggleFeatureAll(feature.permissions)}
                                        disabled={disabled || isFullAccess}
                                    />
                                </Col>
                            </Row>
                        </CardHeader>
                        <CardBody className="py-2">
                            <Row>
                                {feature.permissions.map((permissionId) => (
                                    <Col md="6" lg="4" key={permissionId}>
                                        <FormGroup check>
                                            <CustomInput
                                                type="checkbox"
                                                id={`perm-${feature.feature_id}-${permissionId}-${formType}`}
                                                label={getPermissionLabel(permissionId)}
                                                checked={selected.has(permissionId) || isFullAccess}
                                                onChange={() => togglePermission(permissionId)}
                                                disabled={disabled || isFullAccess}
                                            />
                                        </FormGroup>
                                    </Col>
                                ))}
                            </Row>
                        </CardBody>
                    </Card>
                );
            })}
        </>
    );
};

export default RolePermissionsPicker;
